import { faIcons, faPlus, faTrash } from '@fortawesome/pro-solid-svg-icons';
import { validateValueMappings } from '@supply-mover-2-0/core';
import { useTranslation } from '@supply-mover-2-0/web-ui';
import React, { useEffect, useState } from 'react';
import { Icon } from '../../../components/base/Icon';
import { Text } from '../../../components/base/Text';
import { Card } from '../../../components/display/Card';
import type { TableColumn } from '../../../components/display/Table';
import { Table } from '../../../components/display/Table';
import { Input } from '../../../components/forms/Input';
import { Select } from '../../../components/forms/Select';
import { useThemeColors } from '../../../providers/ThemeProvider';
import { cn } from '../../../utils/cn';
import type { ValueMappingEntry } from '../../types';
import {
	IconDropdown,
	findIconByName,
} from '../list-configuration/columns/IconDropdown';

/**
 * What a mapped value MEANS. The shared token layer supplies the colour, so a
 * badge configured here adapts to light and dark and follows the palette.
 *
 * This replaced a background / text / border swatch picker. Measured before
 * the change: of 1,428 colour values frozen into tenant saved views, every one
 * was either a shipped default or an offered preset — nobody was choosing a
 * bespoke colour, they were freezing a default that then stopped tracking the
 * theme.
 */
const MEANING_OPTIONS = [
	{ value: '', label: 'No colour' },
	{ value: 'success', label: 'Success — finished, and well' },
	{ value: 'info', label: 'Info — moving, nothing wrong' },
	{ value: 'warning', label: 'Warning — waiting on someone' },
	{ value: 'danger', label: 'Danger — failed or was refused' },
	{ value: 'neutral', label: 'Neutral — not started, or stopped' },
];

interface ValueMappingTableProps {
	mappings: ValueMappingEntry[];
	onChange: (mappings: ValueMappingEntry[]) => void;
	isModalOpen?: boolean;
}

export function ValueMappingTable({
	mappings,
	onChange,
	isModalOpen = false,
}: ValueMappingTableProps) {
	const colors = useThemeColors();
	const { t } = useTranslation();
	const [errors, setErrors] = useState<string[]>([]);
	const [confirmDeleteIndex, setConfirmDeleteIndex] = useState<number | null>(
		null,
	);
	const [showIconPicker, setShowIconPicker] = useState<number | null>(null);
	const [iconPickerRefs] = useState<{
		[key: number]: React.RefObject<HTMLButtonElement | null>;
	}>({});

	// Validate on mount and when mappings change
	useEffect(() => {
		const validationErrors = validateValueMappings(mappings);
		setErrors(validationErrors);
	}, [mappings]);

	// Determine which color types to show based on display style
	const handleAddMapping = () => {
		const newMapping: ValueMappingEntry = {
			value: '',
			displayText: undefined,
			color: undefined,
		};
		onChange([...mappings, newMapping]);
	};

	const handleRemoveMapping = (index: number) => {
		const newMappings = mappings.filter((_, i) => i !== index);
		onChange(newMappings);
	};

	const handleUpdateMapping = (
		index: number,
		updates: Partial<ValueMappingEntry>,
	) => {
		const newMappings = [...mappings];
		const current = newMappings[index];
		if (current == null) {
			return;
		}
		newMappings[index] = { ...current, ...updates };
		onChange(newMappings);
	};

	const handleIconChange = (index: number, iconName: string) => {
		handleUpdateMapping(index, { iconName });
		setShowIconPicker(null);
	};

	const handleRemoveIcon = (index: number) => {
		handleUpdateMapping(index, { iconName: undefined });
	};

	// Create refs for icon buttons
	const getIconButtonRef = (index: number) => {
		if (!iconPickerRefs[index]) {
			iconPickerRefs[index] = React.createRef<HTMLButtonElement>();
		}
		return iconPickerRefs[index];
	};

	const renderIconButton = (mapping: ValueMappingEntry, index: number) => {
		const buttonRef = getIconButtonRef(index);
		const iconName = mapping.iconName;
		const iconDefinition = iconName ? findIconByName(iconName) : null;

		return (
			<div className="flex items-center gap-2">
				<button
					ref={buttonRef}
					onClick={() => {
						setShowIconPicker(showIconPicker === index ? null : index);
					}}
					className={cn(
						'flex items-center gap-2 px-3 py-2',
						colors.classes.bgPrimary,
						'border',
						colors.classes.borderDefault,
						'rounded-md',
						colors.classes.bgPrimaryHover,
						'min-w-[100px]',
					)}
				>
					{iconDefinition ? (
						<>
							<Icon
								icon={iconDefinition}
								size="sm"
								color={colors.values.textPrimary}
							/>
							<Text className={cn('text-xs', colors.classes.textPrimary)}>
								{iconName}
							</Text>
						</>
					) : (
						<>
							<Icon icon={faIcons} size="sm" color={colors.values.textMuted} />
							<Text className={cn('text-xs', colors.classes.textSecondary)}>
								{t('Select Icon')}
							</Text>
						</>
					)}
				</button>
				{iconName && (
					<button
						onClick={() => {
							handleRemoveIcon(index);
						}}
						className={cn('p-1', colors.classes.bgPrimaryHover, 'rounded')}
					>
						<Icon icon={faTrash} size="xs" color={colors.values.textMuted} />
					</button>
				)}
			</div>
		);
	};

	// Define table columns
	const columns: TableColumn[] = [
		{
			key: 'originalValue',
			label: 'Original Value',
			width: 150,
			render: (_, item: ValueMappingEntry, index: number) => (
				<Input
					value={String(item.value)}
					onChange={(e) => {
						handleUpdateMapping(index, { value: e.target.value });
					}}
					placeholder="Enter value"
					className="w-full"
					size="sm"
				/>
			),
		},
		{
			key: 'displayText',
			label: 'Display Text',
			width: 150,
			render: (_, item: ValueMappingEntry, index: number) => (
				<Input
					value={item.displayText || ''}
					onChange={(e) => {
						handleUpdateMapping(index, {
							displayText: e.target.value || undefined,
						});
					}}
					placeholder="No change"
					className="w-full"
					size="sm"
				/>
			),
		},
		{
			key: 'icon',
			label: 'Icon',
			width: 150,
			render: (_, item: ValueMappingEntry, index: number) =>
				renderIconButton(item, index),
		},
		{
			key: 'colors',
			label: 'Meaning',
			width: 220,
			render: (_, item: ValueMappingEntry, index: number) => (
				<Select
					value={item.color?.type ?? ''}
					options={MEANING_OPTIONS}
					onChange={(value: string) => {
						const next = [...mappings];
						// The whole colour is replaced rather than merged: an authored
						// bg/text WINS over a meaning by design, so a leftover hex from
						// an older save would silently override the choice made here.
						next[index] = {
							...item,
							color: value === '' ? undefined : { type: value },
						};
						onChange(next);
					}}
				/>
			),
		},
		{
			key: 'actions',
			label: '',
			width: 50,
			align: 'center',
			render: (_, _item: ValueMappingEntry, index: number) => (
				<div className="flex items-center justify-center">
					<button
						onClick={(e) => {
							e.stopPropagation();
							if (confirmDeleteIndex === index) {
								handleRemoveMapping(index);
								setConfirmDeleteIndex(null);
							} else {
								setConfirmDeleteIndex(index);
							}
						}}
						className={cn('p-2', colors.classes.bgPrimaryHover, 'rounded')}
						data-delete-button="true"
					>
						<Icon
							icon={faTrash}
							size="sm"
							color={
								confirmDeleteIndex === index
									? colors.values.danger
									: colors.values.textMuted
							}
						/>
					</button>
				</div>
			),
		},
	];

	// Add click handler to reset delete confirmation
	useEffect(() => {
		const handleClick = (e: MouseEvent) => {
			// Check if click is outside delete buttons
			const target = e.target as HTMLElement;
			if (!target.closest('[data-delete-button]')) {
				setConfirmDeleteIndex(null);
			}
		};

		document.addEventListener('click', handleClick);
		return () => {
			document.removeEventListener('click', handleClick);
		};
	}, []);

	return (
		<div className="flex flex-col">
			{/* Table */}
			<Table
				columns={columns}
				data={mappings}
				height={300}
				stickyHeader={false}
			/>

			{/* Add Button */}
			<button
				onClick={handleAddMapping}
				className={cn(
					'flex items-center justify-center w-full py-3',
					colors.classes.bgSecondary,
					'border-t',
					colors.classes.borderDefault,
					'rounded-b-md',
					colors.classes.bgPrimaryHover,
				)}
			>
				<Icon
					icon={faPlus}
					size="xs"
					color={colors.values.textMuted}
					className="mr-1.5"
				/>
				<Text className={cn('text-sm', colors.classes.textSecondary)}>
					Add Mapping
				</Text>
			</button>

			{/* Validation Errors */}
			{errors.length > 0 && (
				<Card className={cn('mt-2 p-2', colors.classes.bgErrorLight)}>
					{errors.map((error, index) => {
						// Create a stable key using error content and index
						const errorKey = `error_${error.slice(0, 20)}_${index}`;
						return (
							<Text
								key={errorKey}
								className={cn(
									'text-xs',
									colors.classes.textError,
									'mb-1 last:mb-0',
								)}
							>
								• {error}
							</Text>
						);
					})}
				</Card>
			)}

			{/* Color Picker */}

			{/* Icon Dropdown */}
			{showIconPicker !== null && (
				<IconDropdown
					visible={true}
					onClose={() => {
						setShowIconPicker(null);
					}}
					onSelect={(iconName) => {
						handleIconChange(showIconPicker, iconName);
					}}
					currentIcon={{ name: mappings[showIconPicker]?.iconName }}
					triggerRef={
						getIconButtonRef(showIconPicker) as React.RefObject<HTMLElement>
					}
					isModalOpen={isModalOpen}
				/>
			)}
		</div>
	);
}
